import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import type { Table, Zone } from "@/lib/pos-types";
import {
  MousePointer2,
  PlusCircle,
  Grid3X3,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Trash2,
  Edit2,
  Plus,
  Circle,
  Square,
  RectangleHorizontal,
} from "lucide-react";

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────
const GRID = 40;
const SIZES: Record<number, { w: number; h: number; label: string }> = {
  1: { w: 1, h: 1, label: "Pequeña" },
  2: { w: 2, h: 1, label: "Mediana" },
  3: { w: 2, h: 2, label: "Grande" },
};

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────
type Tool = "select" | "add";
type Shape = "square" | "round" | "rect";

interface EditorTable {
  id: string;
  gx: number;
  gy: number;
  shape: Shape;
  size: 1 | 2 | 3;
  num: number;
  cap: number;
}

interface ZoneTableEditorProps {
  readonly?: boolean;
  tables: Table[];
  zoneItems: Zone[];
  zones: string[];
  onTableCreate?: (payload: {
    number: number;
    capacity: number;
    zone: string;
    layoutGx?: number;
    layoutGy?: number;
    layoutSize?: number;
    layoutShape?: string;
  }) => Promise<void>;
  onTableUpdate?: (
    tableId: string,
    patch: Partial<Table>,
  ) => void | Promise<void>;
  onTableDelete?: (tableId: string) => Promise<void>;
  onZoneCreate?: (payload: {
    name: string;
    order: number;
    layoutWidth?: number;
    layoutHeight?: number;
  }) => Promise<void>;
  onZoneUpdate?: (
    zoneId: string,
    patch: {
      name?: string;
      order?: number;
      layoutWidth?: number;
      layoutHeight?: number;
    },
  ) => Promise<void>;
  onZoneDelete?: (zoneId: string) => Promise<void>;
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────
export function ZoneTableEditor({
  readonly = false,
  tables,
  zoneItems,
  zones,
  onTableCreate,
  onTableUpdate,
  onTableDelete,
  onZoneCreate,
  onZoneUpdate,
  onZoneDelete,
}: ZoneTableEditorProps) {
  // Zone state
  const [currentZoneIdx, setCurrentZoneIdx] = useState(0);
  const [zoneFormOpen, setZoneFormOpen] = useState(false);
  const [zoneFormMode, setZoneFormMode] = useState<"create" | "edit">("create");
  const [zoneFormName, setZoneFormName] = useState("");
  const [zoneFormOrder, setZoneFormOrder] = useState("0");
  const [zoneDeleteOpen, setZoneDeleteOpen] = useState(false);

  const [tableStatusDialogOpen, setTableStatusDialogOpen] = useState(false);
  const [tableBlockedReason, setTableBlockedReason] = useState("");
  const [tableReleaseConfirmOpen, setTableReleaseConfirmOpen] = useState(false);

  // Canvas state
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);
  const tableCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [tool, setTool] = useState<Tool>("select");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Add-mode props (defaults for new tables)
  const [addShape, setAddShape] = useState<Shape>("square");
  const [addSize, setAddSize] = useState<1 | 2 | 3>(1);
  const [addNum, setAddNum] = useState(1);
  const [addCap, setAddCap] = useState(4);
  const [jumpNum, setJumpNum] = useState("");

  // Selected-table editable props
  const [propShape, setPropShape] = useState<Shape>("square");
  const [propSize, setPropSize] = useState<1 | 2 | 3>(1);
  const [propNum, setPropNum] = useState(1);
  const [propCap, setPropCap] = useState(4);
  const [propZone, setPropZone] = useState("");

  // Drag
  const draggingRef = useRef<{ id: string; offX: number; offY: number } | null>(
    null,
  );
  const pendingTableRef = useRef<
    Map<
      string,
      {
        gx?: number;
        gy?: number;
        shape?: Shape;
        size?: 1 | 2 | 3;
        zone?: string;
        num?: number;
        cap?: number;
        ts: number;
      }
    >
  >(new Map());
  const pendingCreateRef = useRef<
    Map<string, { zone: string; table: EditorTable; ts: number }>
  >(new Map());
  const pendingFocusRef = useRef<{ id: string; ts: number } | null>(null);

  // Local editor tables (derived from props, but editable via drag/create)
  const [editorTables, setEditorTables] = useState<EditorTable[]>([]);

  // ─── Sorted zones ───
  const sortedZones = [...zoneItems].sort((a, b) => a.order - b.order);
  const currentZone = sortedZones[currentZoneIdx] ?? null;
  const currentZoneName = currentZone?.name ?? zones[currentZoneIdx] ?? "";

  // ─── Sync tables from props to editor ───
  useEffect(() => {
    const now = Date.now();
    const ttlMs = 8000;
    const createTtlMs = 20000;
    const mapped: EditorTable[] = tables
      .filter((t) => {
        const pending = pendingTableRef.current.get(t.id);
        const effectiveZone = pending?.zone ?? t.zone;
        return effectiveZone === currentZoneName;
      })
      .map((t) => {
        const pending = pendingTableRef.current.get(t.id);
        const gxFromApi = t.layoutGx ?? 0;
        const gyFromApi = t.layoutGy ?? 0;
        const shapeFromApi = (t.layoutShape ?? "square") as Shape;
        const sizeFromApi = (t.layoutSize ?? 1) as 1 | 2 | 3;
        const zoneFromApi = t.zone;
        const numFromApi = t.number;
        const capFromApi = t.capacity;

        if (pending) {
          const gxMatch = pending.gx === undefined || pending.gx === gxFromApi;
          const gyMatch = pending.gy === undefined || pending.gy === gyFromApi;
          const shapeMatch =
            pending.shape === undefined || pending.shape === shapeFromApi;
          const sizeMatch =
            pending.size === undefined || pending.size === sizeFromApi;
          const zoneMatch =
            pending.zone === undefined || pending.zone === zoneFromApi;
          const numMatch =
            pending.num === undefined || pending.num === numFromApi;
          const capMatch =
            pending.cap === undefined || pending.cap === capFromApi;
          const expired = now - pending.ts > ttlMs;
          if (
            (gxMatch &&
              gyMatch &&
              shapeMatch &&
              sizeMatch &&
              zoneMatch &&
              numMatch &&
              capMatch) ||
            expired
          ) {
            pendingTableRef.current.delete(t.id);
          }
        }

        const pendingNow = pendingTableRef.current.get(t.id);
        return {
          id: t.id,
          gx: pendingNow?.gx !== undefined ? pendingNow.gx : gxFromApi,
          gy: pendingNow?.gy !== undefined ? pendingNow.gy : gyFromApi,
          shape:
            pendingNow?.shape !== undefined ? pendingNow.shape : shapeFromApi,
          size: pendingNow?.size !== undefined ? pendingNow.size : sizeFromApi,
          num: pendingNow?.num !== undefined ? pendingNow.num : numFromApi,
          cap: pendingNow?.cap !== undefined ? pendingNow.cap : capFromApi,
        };
      });
    const merged = [...mapped];
    for (const [localId, p] of pendingCreateRef.current.entries()) {
      const expired = now - p.ts > createTtlMs;
      if (expired) {
        pendingCreateRef.current.delete(localId);
        continue;
      }

      const created =
        tables.find((t) => {
          if (t.zone !== p.zone) return false;
          if (t.number !== p.table.num) return false;
          if ((t.layoutGx ?? 0) !== p.table.gx) return false;
          if ((t.layoutGy ?? 0) !== p.table.gy) return false;
          if (((t.layoutSize ?? 1) as 1 | 2 | 3) !== p.table.size) return false;
          if (((t.layoutShape ?? "square") as Shape) !== p.table.shape)
            return false;
          return true;
        }) ?? null;

      if (created) {
        pendingCreateRef.current.delete(localId);
        if (selectedId === localId) {
          setSelectedId(created.id);
        }
        continue;
      }

      if (p.zone === currentZoneName) {
        merged.push(p.table);
      }
    }

    setEditorTables(merged);
    if (!selectedId) return;

    const stillHere = merged.find((t) => t.id === selectedId) ?? null;
    if (!stillHere) {
      setSelectedId(null);
      return;
    }

    setPropShape(stillHere.shape);
    setPropSize(stillHere.size);
    setPropNum(stillHere.num);
    setPropCap(stillHere.cap);

    const rec = tables.find((t) => t.id === selectedId) ?? null;
    const pending = pendingTableRef.current.get(selectedId);
    setPropZone(pending?.zone ?? rec?.zone ?? currentZoneName);
  }, [tables, currentZoneName, selectedId]);

  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    if (Date.now() - pending.ts > 8000) {
      pendingFocusRef.current = null;
      return;
    }
    const t = editorTables.find((et) => et.id === pending.id) ?? null;
    if (!t) return;
    const sc = canvasScrollRef.current;
    if (!sc) return;
    const sz = SIZES[t.size];
    const cx = (t.gx * GRID + (sz.w * GRID) / 2) * zoom;
    const cy = (t.gy * GRID + (sz.h * GRID) / 2) * zoom;
    sc.scrollTo({
      left: Math.max(0, cx - sc.clientWidth / 2),
      top: Math.max(0, cy - sc.clientHeight / 2),
      behavior: "smooth",
    });
    pendingFocusRef.current = null;
  }, [editorTables, zoom]);

  // ─── Auto-advance addNum ───
  useEffect(() => {
    const maxNum = tables.reduce((m, t) => Math.max(m, t.number), 0);
    setAddNum(maxNum + 1);
  }, [tables]);

  // ─────────────────── Canvas dimensions ───────────────────
  const CW = currentZone?.layoutWidth ?? 620;
  const CH = currentZone?.layoutHeight ?? 420;

  // ─────────────────── Draw ───────────────────
  const drawGrid = useCallback(() => {
    const gc = gridCanvasRef.current;
    if (!gc) return;
    const ctx = gc.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, gc.width, gc.height);
    if (!showGrid) return;
    ctx.strokeStyle = "rgba(255,255,255,0.07)";
    ctx.lineWidth = 0.5;
    const step = GRID * zoom;
    for (let x = 0; x <= gc.width; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, gc.height);
      ctx.stroke();
    }
    for (let y = 0; y <= gc.height; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(gc.width, y);
      ctx.stroke();
    }
  }, [showGrid, zoom]);

  const drawTable = useCallback(
    (ctx: CanvasRenderingContext2D, t: EditorTable, selected: boolean) => {
      const sz = SIZES[t.size];
      const x = t.gx * GRID * zoom;
      const y = t.gy * GRID * zoom;
      const w = sz.w * GRID * zoom;
      const h = sz.h * GRID * zoom;
      const pad = 5 * zoom;

      // Colors (dark theme)
      const fillColor = selected ? "#1a4a7a" : "#1e1e2e";
      const strokeColor = selected ? "#4a9eff" : "rgba(255,255,255,0.18)";
      const textColor = selected ? "#b5d4f4" : "rgba(255,255,255,0.85)";
      const subColor = selected ? "#7eb8f7" : "rgba(255,255,255,0.4)";

      ctx.save();
      if (selected) {
        ctx.shadowColor = "rgba(74,158,255,0.5)";
        ctx.shadowBlur = 10 * zoom;
      }
      ctx.fillStyle = fillColor;
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = selected ? 2 : 1;

      if (t.shape === "round") {
        const cx = x + w / 2,
          cy = y + h / 2,
          r = Math.min(w, h) / 2 - pad;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // chairs
        const chairs = t.cap <= 2 ? 2 : t.cap <= 4 ? 4 : Math.min(t.cap, 8);
        ctx.fillStyle = "rgba(255,255,255,0.14)";
        const cr = 5 * zoom;
        for (let i = 0; i < chairs; i++) {
          const ang = (i / chairs) * Math.PI * 2 - Math.PI / 2;
          ctx.beginPath();
          ctx.arc(
            cx + Math.cos(ang) * (r + cr + 2 * zoom),
            cy + Math.sin(ang) * (r + cr + 2 * zoom),
            cr,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
      } else {
        const rx = 8 * zoom;
        ctx.beginPath();
        ctx.roundRect(x + pad, y + pad, w - pad * 2, h - pad * 2, rx);
        ctx.fill();
        ctx.stroke();
        // chairs
        const cr = 5 * zoom;
        const topCount =
          t.shape === "rect"
            ? Math.min(3, Math.ceil(t.cap / 2))
            : Math.min(2, Math.ceil(t.cap / 2));
        const botCount = t.cap - topCount;
        ctx.fillStyle = "rgba(255,255,255,0.14)";
        for (let i = 0; i < topCount; i++) {
          ctx.beginPath();
          ctx.arc(
            x + pad + ((w - pad * 2) / (topCount + 1)) * (i + 1),
            y - cr - 2 * zoom,
            cr,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
        for (let i = 0; i < Math.min(botCount, topCount); i++) {
          ctx.beginPath();
          ctx.arc(
            x +
              pad +
              ((w - pad * 2) / (Math.min(botCount, topCount) + 1)) * (i + 1),
            y + h + cr + 2 * zoom,
            cr,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
      }

      ctx.restore();
      const cx = x + w / 2,
        cy = y + h / 2;
      ctx.fillStyle = textColor;
      ctx.font = `bold ${Math.round(12 * zoom)}px system-ui,sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${t.num}`, cx, cy - 7 * zoom);
      ctx.font = `${Math.round(10 * zoom)}px system-ui,sans-serif`;
      ctx.fillStyle = subColor;
      ctx.fillText(`${t.cap}p`, cx, cy + 8 * zoom);
    },
    [zoom],
  );

  const drawTables = useCallback(() => {
    const tc = tableCanvasRef.current;
    if (!tc) return;
    const ctx = tc.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, tc.width, tc.height);
    editorTables.forEach((t) => {
      if (t.id !== selectedId) drawTable(ctx, t, false);
    });
    const sel = editorTables.find((t) => t.id === selectedId);
    if (sel) drawTable(ctx, sel, true);
  }, [editorTables, selectedId, drawTable]);

  // Resize + redraw on deps change
  useEffect(() => {
    const W = Math.ceil(CW * zoom);
    const H = Math.ceil(CH * zoom);
    [gridCanvasRef, tableCanvasRef].forEach((ref) => {
      const c = ref.current;
      if (!c) return;
      c.width = W;
      c.height = H;
      c.style.width = `${W}px`;
      c.style.height = `${H}px`;
    });
    drawGrid();
    drawTables();
  }, [zoom, showGrid, editorTables, selectedId, CW, CH, drawGrid, drawTables]);

  // ─────────────────── Hit-test ───────────────────
  const getTableAt = (px: number, py: number): EditorTable | null => {
    for (let i = editorTables.length - 1; i >= 0; i--) {
      const t = editorTables[i];
      const sz = SIZES[t.size];
      const x = t.gx * GRID * zoom,
        y = t.gy * GRID * zoom;
      const w = sz.w * GRID * zoom,
        h = sz.h * GRID * zoom;
      if (t.shape === "round") {
        const cx = x + w / 2,
          cy = y + h / 2,
          r = Math.min(w, h) / 2;
        if (Math.hypot(px - cx, py - cy) <= r) return t;
      } else {
        if (px >= x && px <= x + w && py >= y && py <= y + h) return t;
      }
    }
    return null;
  };

  // ─────────────────── Selection helpers ───────────────────
  const showPropsFor = (t: EditorTable) => {
    setPropShape(t.shape);
    setPropSize(t.size);
    setPropNum(t.num);
    setPropCap(t.cap);
    setPropZone(currentZoneName);
  };

  // ─────────────────── Canvas events ───────────────────
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (readonly) return;

    const rect = tableCanvasRef.current!.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    if (tool === "add") {
      const gx = Math.floor(px / (GRID * zoom));
      const gy = Math.floor(py / (GRID * zoom));

      const sz = SIZES[addSize];
      const maxGx = Math.floor(CW / GRID) - sz.w;
      const maxGy = Math.floor(CH / GRID) - sz.h;

      if (onTableCreate) {
        onTableCreate({
          capacity: addCap,
          zone: currentZoneName,
          layoutGx: Math.min(gx, maxGx),
          layoutGy: Math.min(gy, maxGy),
          layoutSize: addSize,
          layoutShape: addShape,
        })
          .then((created) => {
            const newTable: EditorTable = {
              id: created.id,
              gx: created.layoutGx,
              gy: created.layoutGy,
              shape: created.layoutShape,
              size: created.layoutSize,
              num: created.number,
              cap: created.capacity,
            };

            setEditorTables((prev) => [...prev, newTable]);
            setSelectedId(created.id);
            showPropsFor(newTable);
            setTool("select");
          })
          .catch((err: unknown) => {
            const msg =
              (err as { response?: { data?: { message?: string } } })?.response
                ?.data?.message ??
              (err instanceof Error ? err.message : "Error al crear mesa");

            toast.error(msg);
          });
      }

      return;
    }

    const t = getTableAt(px, py);

    if (t) {
      setSelectedId(t.id);

      showPropsFor(t);

      draggingRef.current = {
        id: t.id,
        offX: px - t.gx * GRID * zoom,
        offY: py - t.gy * GRID * zoom,
      };
    } else {
      setSelectedId(null);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (readonly) return;
    const dr = draggingRef.current;
    if (!dr) return;
    const rect = tableCanvasRef.current!.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    setEditorTables((prev) =>
      prev.map((t) => {
        if (t.id !== dr.id) return t;
        const sz = SIZES[t.size];
        const rawGx = (px - dr.offX) / (GRID * zoom);
        const rawGy = (py - dr.offY) / (GRID * zoom);
        return {
          ...t,
          gx: Math.max(
            0,
            Math.min(Math.round(rawGx), Math.floor(CW / GRID) - sz.w),
          ),
          gy: Math.max(
            0,
            Math.min(Math.round(rawGy), Math.floor(CH / GRID) - sz.h),
          ),
        };
      }),
    );
  };

  const handleMouseUp = () => {
    if (readonly) return;

    const dr = draggingRef.current;
    if (dr) {
      const t = editorTables.find((et) => et.id === dr.id);
      if (t && onTableUpdate) {
        const prev = pendingTableRef.current.get(t.id);
        pendingTableRef.current.set(t.id, {
          ...prev,
          gx: t.gx,
          gy: t.gy,
          ts: Date.now(),
        });
        void onTableUpdate(t.id, { layoutGx: t.gx, layoutGy: t.gy });
      }
    }
    draggingRef.current = null;
  };

  // ─────────────────── Prop updates ───────────────────
  const updateSelectedProp = (
    key: keyof EditorTable,
    value: number | string,
  ) => {
    if (readonly) return;

    setEditorTables((prev) =>
      prev.map((t) => {
        if (t.id !== selectedId) return t;
        const updated = { ...t, [key]: value };
        if (onTableUpdate) {
          const patch: Partial<Table> = {};
          if (key === "num") patch.number = value as number;
          if (key === "cap") patch.capacity = value as number;
          if (key === "shape")
            patch.layoutShape = value as "square" | "round" | "rect";
          if (key === "size") patch.layoutSize = value as 1 | 2 | 3;
          if (key === "num" || key === "cap") {
            const prevPending = pendingTableRef.current.get(t.id);
            pendingTableRef.current.set(t.id, {
              ...prevPending,
              num: key === "num" ? (value as number) : prevPending?.num,
              cap: key === "cap" ? (value as number) : prevPending?.cap,
              ts: Date.now(),
            });
          }
          if (key === "shape" || key === "size") {
            const prevPending = pendingTableRef.current.get(t.id);
            pendingTableRef.current.set(t.id, {
              ...prevPending,
              shape: key === "shape" ? (value as Shape) : prevPending?.shape,
              size: key === "size" ? (value as 1 | 2 | 3) : prevPending?.size,
              ts: Date.now(),
            });
          }
          void onTableUpdate(t.id, patch);
        }
        return updated;
      }),
    );
  };

  // ─────────────────── Delete selected ───────────────────
  const handleDeleteSelected = useCallback(async () => {
    if (readonly) return;

    if (!selectedId) return;
    const isLocal = selectedId.startsWith("local-");
    if (!isLocal && onTableDelete) {
      try {
        await onTableDelete(selectedId);
      } catch {
        toast.error("Error al eliminar mesa");
        return;
      }
    }
    pendingTableRef.current.delete(selectedId);
    setEditorTables((prev) => prev.filter((t) => t.id !== selectedId));
    setSelectedId(null);
    toast.success("Mesa eliminada");
  }, [onTableDelete, selectedId]);

  // ─────────────────── Zone CRUD ───────────────────
  const openCreateZone = () => {
    setZoneFormMode("create");
    setZoneFormName("");
    setZoneFormOrder(String(sortedZones.length));
    setZoneFormOpen(true);
  };

  const openEditZone = () => {
    if (!currentZone) return;
    setZoneFormMode("edit");
    setZoneFormName(currentZone.name);
    setZoneFormOrder(String(currentZone.order));
    setZoneFormOpen(true);
  };

  const submitZoneForm = async (e: React.FormEvent) => {
    if (readonly) return;
    e.preventDefault();
    if (zoneFormMode === "create") {
      try {
        await onZoneCreate?.({
          name: zoneFormName,
          order: Number(zoneFormOrder),
          layoutWidth: 620,
          layoutHeight: 420,
        });
        toast.success("Zona creada");
        setZoneFormOpen(false);
      } catch {
        toast.error("Error al crear zona");
      }
    } else if (currentZone) {
      try {
        await onZoneUpdate?.(currentZone.id, {
          name: zoneFormName,
          order: Number(zoneFormOrder),
        });
        toast.success("Zona actualizada");
        setZoneFormOpen(false);
      } catch {
        toast.error("Error al actualizar zona");
      }
    }
  };

  const handleDeleteZone = async () => {
    if (readonly) return;
    if (!currentZone) return;
    try {
      await onZoneDelete?.(currentZone.id);
      setCurrentZoneIdx(0);
      toast.success("Zona eliminada");
    } catch {
      toast.error("Error al eliminar zona");
    }
    setZoneDeleteOpen(false);
  };

  // ─────────────────── Keyboard shortcuts ───────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && tool === "add") setTool("select");
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        const active = document.activeElement;
        if (
          active &&
          (active.tagName === "INPUT" || active.tagName === "TEXTAREA")
        )
          return;
        void handleDeleteSelected();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleDeleteSelected, selectedId, tool]);

  const selectedTable = editorTables.find((t) => t.id === selectedId) ?? null;
  const selectedTableRecord = tables.find((t) => t.id === selectedId) ?? null;
  const zoneNames = useMemo<string[]>(
    () => sortedZones.map((z) => z.name),
    [sortedZones],
  );

  useEffect(() => {
    if (!selectedId) return;
    const rec = tables.find((t) => t.id === selectedId) ?? null;
    if (rec) setPropZone(rec.zone);
  }, [selectedId, tables]);

  const changeSelectedZone = async (nextZone: string) => {
    if (readonly) return;

    if (!selectedId) return;
    const cur = propZone || currentZoneName;
    if (nextZone === cur) return;
    setPropZone(nextZone);
    const prevPending = pendingTableRef.current.get(selectedId);
    pendingTableRef.current.set(selectedId, {
      ...prevPending,
      zone: nextZone,
      ts: Date.now(),
    });
    try {
      await onTableUpdate?.(selectedId, { zone: nextZone });
    } catch {
      toast.error("No se pudo cambiar la zona");
      pendingTableRef.current.delete(selectedId);
      setPropZone(cur);
      return;
    }
    if (nextZone !== currentZoneName) {
      setEditorTables((prev) => prev.filter((t) => t.id !== selectedId));
      setSelectedId(null);
    }
    toast.success(`Mesa movida a ${nextZone}`);
  };

  const jumpToTableNumber = (raw: string) => {
    const parsed = parseInt(raw, 10);
    const v = Number.isFinite(parsed) ? Math.max(1, Math.floor(parsed)) : 1;
    const found = tables.find((t) => t.number === v) ?? null;
    if (!found) {
      toast.error("Mesa no encontrada", {
        description: `No existe mesa N.° ${v}`,
      });
      return;
    }
    const zoneIndex = sortedZones.findIndex((z) => z.name === found.zone);
    if (zoneIndex >= 0 && zoneIndex !== currentZoneIdx)
      setCurrentZoneIdx(zoneIndex);
    setSelectedId(found.id);
    pendingFocusRef.current = { id: found.id, ts: Date.now() };
  };

  const tableStatusLabel = (status: Table["status"] | undefined) => {
    switch (status) {
      case "available":
        return "Disponible";
      case "occupied":
        return "Ocupada";
      case "pending-payment":
        return "Por pagar";
      case "blocked":
        return "Bloqueada";
      default:
        return "—";
    }
  };

  const requestTableStatusChange = (nextStatus: Table["status"]) => {
    if (readonly) return;
    if (!selectedId) return;
    if (nextStatus === "blocked") {
      setTableBlockedReason(selectedTableRecord?.blockedReason ?? "");
      setTableStatusDialogOpen(true);
      return;
    }
    if (
      nextStatus === "available" &&
      selectedTableRecord?.status !== "available"
    ) {
      setTableReleaseConfirmOpen(true);
      return;
    }
    void onTableUpdate?.(selectedId, { status: nextStatus })
      ?.then?.(() => toast.success("Mesa actualizada"))
      ?.catch?.(() => toast.error("No se pudo actualizar la mesa"));
  };

  const confirmReleaseTable = () => {
    if (readonly) return;
    if (!selectedId) return;
    void onTableUpdate?.(selectedId, { status: "available" })
      ?.then?.(() => toast.success("Mesa liberada"))
      ?.catch?.(() => toast.error("No se pudo liberar la mesa"));
    setTableReleaseConfirmOpen(false);
  };

  const confirmBlockTable = () => {
    if (readonly) return;
    if (!selectedId) return;
    const reason = tableBlockedReason.trim();
    if (!reason) {
      toast.error("Ingresa un motivo para bloquear");
      return;
    }
    void onTableUpdate?.(selectedId, {
      status: "blocked",
      blockedReason: reason,
    })
      ?.then?.(() => toast.success("Mesa bloqueada"))
      ?.catch?.(() => toast.error("No se pudo bloquear la mesa"));
    setTableStatusDialogOpen(false);
  };

  // ─────────────────── Cursor ───────────────────
  const getCursor = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool === "add") return "crosshair";
    if (draggingRef.current) return "grabbing";
    const rect = tableCanvasRef.current?.getBoundingClientRect();
    if (!rect) return "default";
    const t = getTableAt(e.clientX - rect.left, e.clientY - rect.top);
    return t ? "grab" : "default";
  };

  return (
    <div className="flex flex-col h-full gap-0 rounded-2xl overflow-hidden border border-border/50 bg-card/40 backdrop-blur-sm shadow-2xl">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 sm:py-2.5 bg-card/60 border-b border-border/30 shrink-0">
        {/* Tool group */}
        {!readonly && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest mr-1">
              Herramienta
            </span>
            <button
              onClick={() => setTool("select")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                tool === "select"
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background/50 text-muted-foreground border-border/50 hover:text-foreground hover:bg-muted/40",
              )}
            >
              <MousePointer2 className="w-3.5 h-3.5" /> Seleccionar
            </button>
            <button
              onClick={() => setTool("add")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                tool === "add"
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background/50 text-muted-foreground border-border/50 hover:text-foreground hover:bg-muted/40",
              )}
            >
              <PlusCircle className="w-3.5 h-3.5" /> Agregar mesa
            </button>
          </div>
        )}

        <div className="w-px h-5 bg-border/50 mx-1" />

        {/* Vista group */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest mr-1">
            Vista
          </span>
          <button
            onClick={() => setShowGrid((g) => !g)}
            className={cn(
              "p-1.5 rounded-lg border text-xs transition-all",
              showGrid
                ? "bg-primary/20 border-primary/40 text-primary"
                : "bg-background/50 border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/40",
            )}
          >
            <Grid3X3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
            className="p-1.5 rounded-lg border bg-background/50 border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
            className="p-1.5 rounded-lg border bg-background/50 border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setZoom(1)}
            className="px-2.5 py-1.5 rounded-lg border bg-background/50 border-border/50 text-muted-foreground text-[11px] font-medium hover:text-foreground hover:bg-muted/40 transition-all"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="w-px h-5 bg-border/50 mx-1" />

        {/* Actions */}
        <div className="flex items-center gap-1.5 ml-auto">
          {!readonly && selectedId && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border bg-destructive/10 border-destructive/30 text-destructive hover:bg-destructive/20 transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminar mesa
            </button>
          )}
          <Badge variant="secondary" className="font-mono text-[11px]">
            {editorTables.length} mesa{editorTables.length !== 1 ? "s" : ""}
          </Badge>
          <Badge variant="outline" className="text-[11px] font-mono">
            {Math.round(zoom * 100)}%
          </Badge>
        </div>
      </div>

      {/* ── Zone Tabs ── */}
      <div className="flex items-center gap-0 px-2 bg-card/40 border-b border-border/30 overflow-x-auto no-scrollbar shrink-0">
        {sortedZones.map((zone, i) => (
          <button
            key={zone.id}
            onClick={() => {
              setCurrentZoneIdx(i);
              setSelectedId(null);
            }}
            className={cn(
              "px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-all",
              i === currentZoneIdx
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {zone.name}
          </button>
        ))}
        {/* Zone actions */}
        {!readonly && (
          <div className="flex items-center gap-1 px-2 ml-auto shrink-0">
            {currentZone && (
              <>
                <button
                  onClick={openEditZone}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all"
                  title="Editar zona"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setZoneDeleteOpen(true)}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                  title="Eliminar zona"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            <button
              onClick={openCreateZone}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Zona
            </button>
          </div>
        )}
      </div>

      {/* ── Canvas ── */}
      <div
        ref={canvasScrollRef}
        className="flex-1 relative overflow-auto bg-[#0e0e1a]"
        style={{ minHeight: 0 }}
      >
        {sortedZones.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Grid3X3 className="w-8 h-8 text-primary/40" />
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              No hay zonas configuradas. Crea una zona para comenzar a diseñar
              el layout del salón.
            </p>
            <Button size="sm" onClick={openCreateZone}>
              <Plus className="w-4 h-4 mr-1" /> Crear primera zona
            </Button>
          </div>
        ) : (
          <div
            className="relative"
            style={{
              width: `${Math.ceil(CW * zoom)}px`,
              height: `${Math.ceil(CH * zoom)}px`,
            }}
          >
            <canvas
              ref={gridCanvasRef}
              className="absolute top-0 left-0 pointer-events-none"
            />
            <canvas
              ref={tableCanvasRef}
              className="absolute top-0 left-0"
              onMouseDown={handleMouseDown}
              onMouseMove={(e) => {
                handleMouseMove(e);
                if (tableCanvasRef.current)
                  tableCanvasRef.current.style.cursor = getCursor(e);
              }}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          </div>
        )}
      </div>

      {/* ── Properties Panel ── */}
      {!readonly && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 sm:py-2.5 bg-card/60 border-t border-border/30 shrink-0">
          {/* Shape */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
              Forma
            </span>
            {(["square", "round", "rect"] as Shape[]).map((s) => {
              const active = selectedTable ? propShape === s : addShape === s;
              return (
                <button
                  key={s}
                  title={
                    s === "square"
                      ? "Cuadrada"
                      : s === "round"
                        ? "Redonda"
                        : "Rectangular"
                  }
                  onClick={() => {
                    if (selectedTable) {
                      setPropShape(s);
                      updateSelectedProp("shape", s);
                    } else setAddShape(s);
                  }}
                  className={cn(
                    "w-7 h-7 rounded-lg border flex items-center justify-center transition-all",
                    active
                      ? "bg-primary/20 border-primary text-primary"
                      : "border-border/50 text-muted-foreground hover:border-border hover:bg-muted/40",
                  )}
                >
                  {s === "square" && <Square className="w-3.5 h-3.5" />}
                  {s === "round" && <Circle className="w-3.5 h-3.5" />}
                  {s === "rect" && (
                    <RectangleHorizontal className="w-3.5 h-3.5" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="w-px h-5 bg-border/30" />

          {/* Jump to table */}
          {!readonly && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                Ir a mesa
              </span>
              <input
                type="number"
                min={1}
                max={99}
                value={jumpNum}
                onChange={(e) => {
                  setJumpNum(e.target.value);
                }}
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (jumpNum.trim().length === 0) return;
                  jumpToTableNumber(jumpNum);
                }}
                onBlur={() => {
                  if (jumpNum.trim().length === 0) return;
                  jumpToTableNumber(jumpNum);
                }}
                className="w-14 px-2 py-1 text-xs rounded-md border border-border/50 bg-background/50 text-foreground focus:outline-none focus:border-primary/50"
              />
            </div>
          )}

          {/* Capacity */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
              Comensales
            </span>
            <input
              type="number"
              min={1}
              max={20}
              value={selectedTable ? propCap : addCap}
              onChange={(e) => {
                const v = parseInt(e.target.value) || 1;
                if (selectedTable) {
                  setPropCap(v);
                  updateSelectedProp("cap", v);
                } else setAddCap(v);
              }}
              className="w-14 px-2 py-1 text-xs rounded-md border border-border/50 bg-background/50 text-foreground focus:outline-none focus:border-primary/50"
            />
          </div>

          {/* Size */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
              Tamaño
            </span>
            <input
              type="range"
              min={1}
              max={3}
              step={1}
              value={selectedTable ? propSize : addSize}
              onChange={(e) => {
                const v = parseInt(e.target.value) as 1 | 2 | 3;
                if (selectedTable) {
                  setPropSize(v);
                  updateSelectedProp("size", v);
                } else setAddSize(v);
              }}
              className="w-20 accent-primary"
            />
            <span className="text-[10px] text-muted-foreground min-w-[52px]">
              {SIZES[selectedTable ? propSize : addSize].label}
            </span>
          </div>

          {/* Status hint */}
          {!readonly && (
            <div className="ml-auto flex items-center gap-3">
              {selectedTableRecord && (
                <>
                  <select
                    value={propZone || selectedTableRecord.zone}
                    onChange={(e) => {
                      void changeSelectedZone(e.target.value);
                    }}
                    className="h-8 px-2 text-xs rounded-md border border-border/50 bg-background/50 text-foreground focus:outline-none focus:border-primary/50"
                  >
                    {zoneNames.map((z: string) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                  <Badge variant="outline" className="text-[10px] font-mono">
                    Estado: {tableStatusLabel(selectedTableRecord.status)}
                  </Badge>
                  <select
                    value={selectedTableRecord.status}
                    onChange={(e) =>
                      requestTableStatusChange(
                        e.target.value as Table["status"],
                      )
                    }
                    className="h-8 px-2 text-xs rounded-md border border-border/50 bg-background/50 text-foreground focus:outline-none focus:border-primary/50"
                  >
                    <option value="available">Disponible</option>
                    <option value="occupied">Ocupada</option>
                    <option value="pending-payment">Por pagar</option>
                    <option value="blocked">Bloqueada</option>
                  </select>
                  {selectedTableRecord.status !== "available" && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 text-xs font-bold"
                      onClick={() => requestTableStatusChange("available")}
                    >
                      Liberar
                    </Button>
                  )}
                </>
              )}
              <div className="text-[10px] text-muted-foreground italic">
                {tool === "add"
                  ? "Haz clic en el canvas para colocar · Esc para cancelar"
                  : selectedTable
                    ? `Mesa ${propNum} seleccionada · Arrastra para mover`
                    : 'Selecciona una mesa o usa "Agregar mesa" para colocar'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Zona Form Dialog ── */}
      <Dialog open={zoneFormOpen} onOpenChange={setZoneFormOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {zoneFormMode === "create"
                ? "Nueva Zona"
                : `Editar zona: ${currentZone?.name}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitZoneForm} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Nombre de la zona</Label>
              <Input
                value={zoneFormName}
                onChange={(e) => setZoneFormName(e.target.value)}
                placeholder="Ej: Salón principal, Terraza..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Orden de visualización</Label>
              <Input
                value={zoneFormOrder}
                onChange={(e) => setZoneFormOrder(e.target.value)}
                type="number"
                min={0}
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setZoneFormOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {zoneFormMode === "create" ? "Crear zona" : "Guardar cambios"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Zone Alert ── */}
      <AlertDialog open={zoneDeleteOpen} onOpenChange={setZoneDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Eliminar zona "{currentZone?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Las mesas de esta zona quedarán sin zona asignada. Esta acción no
              se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteZone}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar zona
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={tableReleaseConfirmOpen}
        onOpenChange={setTableReleaseConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Liberar mesa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción marcará la mesa como disponible y limpiará su
              asignación actual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReleaseTable}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Liberar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={tableStatusDialogOpen}
        onOpenChange={setTableStatusDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bloquear mesa</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label>Motivo</Label>
            <Input
              value={tableBlockedReason}
              onChange={(e) => setTableBlockedReason(e.target.value)}
              placeholder="Ej: Mesa fuera de servicio"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTableStatusDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="button" onClick={confirmBlockTable}>
              Bloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
